import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircleIcon, Loader2Icon, MessageCircleIcon } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Applicant } from "@type/applicant";
import {
    previewScreeningInvitationWhatsapp,
    ScreeningInvitationIneligibleReason,
    ScreeningInvitationPreviewMessage,
    ScreeningInvitationSendResult,
    sendScreeningInvitationWhatsapp,
} from "@service/job";
import { catchErrorLog, fatalErrorLog } from "@util/sentry";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    jobId: string;
    applicants: Applicant[];
    /** Fired after a send that reached at least one candidate, so the list can refresh. */
    onSent: (result: ScreeningInvitationSendResult) => void;
}

/**
 * Why the BE excluded a candidate, in HR's words (#3478, codes from BE #4533).
 * An unknown code still renders something honest rather than a raw enum.
 */
const REASON_LABEL: Record<ScreeningInvitationIneligibleReason, string> = {
    missing_phone_number: "No phone number on file",
    invalid_phone_number: "Phone number isn't valid for WhatsApp",
    already_invited: "Already invited",
    wa_not_enabled: "WhatsApp isn't enabled for this workspace",
    template_unavailable: "Message template unavailable right now",
};

const reasonLabel = (reason: ScreeningInvitationIneligibleReason | null): string =>
    (reason && REASON_LABEL[reason]) || "Can't be messaged on WhatsApp";

export default function WhatsappScreeningInvitationDialog({
    isOpen,
    onClose,
    jobId,
    applicants,
    onSent,
}: Props) {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [sendError, setSendError] = useState<string>("");
    const [loadError, setLoadError] = useState<boolean>(false);
    const [messages, setMessages] = useState<ScreeningInvitationPreviewMessage[]>([]);

    const nameById = useMemo(() => {
        const map: Record<string, string> = {};
        applicants.forEach((a) => { map[a.applicant_id] = a.applicant_name; });
        return map;
    }, [applicants]);

    const applicantIds = useMemo(
        () => applicants.map((a) => a.applicant_id),
        [applicants],
    );

    const loadPreview = useCallback(async () => {
        if (!jobId || applicantIds.length === 0) return;
        setIsLoading(true);
        setLoadError(false);
        try {
            const result = await previewScreeningInvitationWhatsapp(jobId, applicantIds);
            setMessages(result?.messages || []);
        } catch (error) {
            catchErrorLog(`Failed to preview WhatsApp screening invitation: ${error}`);
            setLoadError(true);
        } finally {
            setIsLoading(false);
        }
    }, [jobId, applicantIds]);

    useEffect(() => {
        if (isOpen) loadPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const eligible = messages.filter((m) => m.eligible && m.text);
    const excluded = messages.filter((m) => !m.eligible || !m.text);

    const handleSend = async () => {
        if (eligible.length === 0) return;
        setSendError("");
        setIsSending(true);
        try {
            // Send only the candidates the BE cleared — the excluded ones would
            // just come back as failures and muddy the result.
            const result = await sendScreeningInvitationWhatsapp(
                jobId,
                eligible.map((m) => m.applicant_id),
            );
            onSent(result);
            onClose();
        } catch (error) {
            // Two problems fixed here. The message used to interpolate the raw
            // axios error ("…status code 401"), which catchErrorLog drops as
            // routine noise — so a whole failed bulk send left no record. And
            // the dialog simply went quiet: no snackbar, no inline error, while
            // HR believed the invitations had gone out.
            fatalErrorLog(
                "[invitation] WhatsApp bulk screening invitation failed — no candidate was invited",
                {
                    job_id: jobId,
                    candidate_count: eligible.length,
                    http_status: (error as { response?: { status?: number } })?.response?.status ?? "none (network or timeout)",
                    detail: String(error),
                },
                { area: "screening_invitation" },
            );
            setSendError("The invitations could not be sent. Please try again.");
            setIsSending(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isSending) onClose(); }}>
            <DialogContent className="sm:max-w-lg border-0 outline-none ring-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MessageCircleIcon className="w-5 h-5 text-green-600" />
                        Send screening invitation on WhatsApp
                    </DialogTitle>
                    <DialogDescription>
                        Review the message before sending. Candidates move to Pending Assessment once it goes out.
                    </DialogDescription>
                </DialogHeader>

                {isLoading ? (
                    <div className="py-10 flex items-center justify-center">
                        <Loader2Icon className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                ) : loadError ? (
                    <div className="py-6 flex flex-col items-center text-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                            <AlertCircleIcon className="w-6 h-6" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            We couldn't load the message preview right now. Please try again in a moment.
                        </p>
                        <Button variant="outline" onClick={loadPreview}>Try again</Button>
                    </div>
                ) : (
                    <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                        {eligible.length > 0 && (
                            <div className="space-y-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    Message preview
                                </label>
                                {eligible.map((message) => (
                                    <div key={message.applicant_id} className="space-y-1.5">
                                        <div className="flex items-baseline justify-between gap-2">
                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                                {nameById[message.applicant_id] || "Candidate"}
                                            </p>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 tabular-nums">
                                                {message.phone_number}
                                            </span>
                                        </div>
                                        {/* Rendered by the BE from the approved template — shown verbatim. */}
                                        <p className="whitespace-pre-line rounded-md border-l-2 border-green-300 dark:border-green-700 bg-gray-50 dark:bg-gray-800/50 p-3 text-xs text-gray-600 dark:text-gray-400">
                                            {message.text}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {excluded.length > 0 && (
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    Not included ({excluded.length})
                                </label>
                                <ul className="space-y-1">
                                    {excluded.map((message) => (
                                        <li
                                            key={message.applicant_id}
                                            className="flex items-baseline justify-between gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-2"
                                        >
                                            <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                                                {nameById[message.applicant_id] || "Candidate"}
                                            </span>
                                            <span className="text-xs text-amber-700 dark:text-amber-400 flex-shrink-0">
                                                {reasonLabel(message.reason)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {eligible.length === 0 && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                None of the selected candidates can be invited on WhatsApp right now.
                            </p>
                        )}
                    </div>
                )}

                {sendError && (
                    <p className="text-sm text-red-600 dark:text-red-400">{sendError}</p>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isSending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={isLoading || isSending || eligible.length === 0}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {isSending ? (
                            <><Loader2Icon className="w-4 h-4 animate-spin" /> Sending…</>
                        ) : (
                            `Send to ${eligible.length} candidate${eligible.length === 1 ? "" : "s"}`
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
